import React, { useState, useEffect, useCallback } from 'react';

const sectionStyle = { background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '1.2rem 1.4rem', marginTop: '1.2rem' };

function PmsNightAuditAnomaly({ token, selectedProperty }) {
  const [anomalies, setAnomalies] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const headers = useCallback((withJson = false) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...(withJson ? { 'Content-Type': 'application/json' } : {}),
  }), [token, selectedProperty]);

  const loadResults = useCallback(async () => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    try {
      const res = await fetch('/api/v1/ai/night-audit-anomaly/results?status=OPEN', { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load');
      setAnomalies(data.anomalies || []);
      setSummary(data.summary || null);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [selectedProperty?.id, headers]);

  useEffect(() => { loadResults(); }, [loadResults]);

  const scan = async () => {
    setScanning(true); setError(''); setMessage('');
    try {
      const res = await fetch('/api/v1/ai/night-audit-anomaly/scan', { method: 'POST', headers: headers(true), body: '{}' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Scan failed');
      setMessage(data.anomaly_count > 0 ? `Found ${data.anomaly_count} issue(s) for ${data.business_date || 'the last audit'}` : 'No issues found');
      setTimeout(() => setMessage(''), 4000);
      await loadResults();
    } catch (err) { setError(err.message); }
    finally { setScanning(false); }
  };

  const handleAction = async (id, action) => {
    try {
      await fetch(`/api/v1/ai/night-audit-anomaly/results/${id}/action`, {
        method: 'POST', headers: headers(true),
        body: JSON.stringify({ action }),
      });
      setAnomalies(prev => prev.filter(a => a.id !== id));
      if (summary) setSummary({ ...summary, total: summary.total - 1 });
    } catch (_) { /* ignore */ }
  };

  if (!selectedProperty?.id) {
    return (<div style={{ padding: '1rem', maxWidth: 1000 }}><h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Night Audit Anomaly Checks</h2><p style={{ color: 'var(--theme-text-muted)' }}>Please select a property.</p></div>);
  }

  const formatCurrency = (v) => v != null ? `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '-';
  const sevColors = { CRITICAL: 'var(--theme-danger)', HIGH: '#f97316', MEDIUM: '#f59e0b', LOW: 'var(--theme-text-muted)' };
  const typeLabels = {
    MISSED_POSTING: 'Missed Posting', DUPLICATE_POSTING: 'Duplicate Posting', NO_SHOW_REVIEW: 'No-Show Review',
    KPI_SWING: 'KPI Swing', HK_FLIP_GAP: 'Housekeeping Flip Gap', DATE_ROLL_MISMATCH: 'Date Roll Mismatch',
  };

  return (
    <div style={{ padding: '1rem', maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ marginTop: 0, color: 'var(--theme-primary)', marginBottom: 4 }}>Night Audit Anomaly Checks</h2>
          <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.85rem', margin: 0 }}>Rule-based integrity checks that run automatically after every night audit</p>
        </div>
        <button onClick={scan} disabled={scanning} style={{
          padding: '0.5rem 1.2rem', borderRadius: 6, border: 'none', background: scanning ? 'var(--theme-text-muted)' : '#ef4444',
          color: '#fff', fontWeight: 600, cursor: scanning ? 'wait' : 'pointer', fontSize: '0.85rem',
        }}>{scanning ? 'Checking...' : 'Re-check Last Audit'}</button>
      </div>

      {error && <div className="error-banner" style={{ marginTop: '0.5rem' }}>{error}</div>}
      {message && <div className="success-banner" style={{ marginTop: '0.5rem' }}>{message}</div>}

      {loading ? (
        <div style={{ color: 'var(--theme-text-muted)', padding: '2rem', textAlign: 'center' }}>Loading...</div>
      ) : anomalies.length === 0 ? (
        <div style={sectionStyle}><div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--theme-text-muted)' }}>
          <div style={{ fontSize: '1.3rem', marginBottom: '0.5rem' }}>No open issues</div>
          <div style={{ fontSize: '0.85rem' }}>Checks run automatically at the end of every night audit. Use "Re-check Last Audit" to run them again on demand.</div>
        </div></div>
      ) : (
        <>
          {summary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.75rem', marginTop: '1rem' }}>
              <SCard label="Total" value={summary.total} color="var(--theme-primary)" />
              <SCard label="Critical" value={summary.critical} color="var(--theme-danger)" />
              <SCard label="High" value={summary.high} color="#f97316" />
              <SCard label="Medium" value={summary.medium} color="#f59e0b" />
              <SCard label="Potential Impact" value={formatCurrency(summary.total_impact)} color="var(--theme-danger)" />
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1rem' }}>
            {anomalies.map(a => (
              <div key={a.id} style={{ background: '#fff', border: `1px solid ${sevColors[a.severity] || 'var(--theme-border)'}`, borderLeft: `4px solid ${sevColors[a.severity]}`, borderRadius: 8, padding: '1rem 1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 700, background: sevColors[a.severity], color: '#fff' }}>{a.severity}</span>
                      <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600, background: 'var(--theme-border-light)', color: '#475569' }}>{typeLabels[a.anomaly_type] || a.anomaly_type}</span>
                      <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-muted)' }}>{a.business_date}</span>
                    </div>
                    {a.room_number && <div style={{ fontWeight: 700, fontSize: '0.92rem', color: '#1e293b' }}>Room {a.room_number}</div>}
                    <div style={{ fontSize: '0.85rem', color: '#334155', marginTop: 4 }}>{a.description}</div>
                    {(a.expected_value || a.actual_value) && (
                      <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-secondary)', marginTop: 4 }}>
                        {a.expected_value && <span>Expected: <strong>{a.expected_value}</strong></span>}
                        {a.expected_value && a.actual_value && <span> | </span>}
                        {a.actual_value && <span>Actual: <strong style={{ color: 'var(--theme-danger)' }}>{a.actual_value}</strong></span>}
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {a.potential_impact > 0 && <div style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--theme-danger)' }}>{formatCurrency(a.potential_impact)}</div>}
                    <div style={{ display: 'flex', gap: '0.3rem', marginTop: 6 }}>
                      <button onClick={() => handleAction(a.id, 'RESOLVED')} style={{ padding: '0.3rem 0.6rem', borderRadius: 4, border: 'none', background: 'var(--theme-success)', color: '#fff', cursor: 'pointer', fontSize: '0.78rem' }}>Resolve</button>
                      <button onClick={() => handleAction(a.id, 'DISMISSED')} style={{ padding: '0.3rem 0.6rem', borderRadius: 4, border: '1px solid var(--theme-border)', background: '#fff', color: 'var(--theme-text-secondary)', cursor: 'pointer', fontSize: '0.78rem' }}>Dismiss</button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function SCard({ label, value, color }) {
  return (<div style={{ background: '#fff', border: '1px solid var(--theme-border)', borderRadius: 8, padding: '0.75rem 1rem', textAlign: 'center' }}>
    <div style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1.3rem', fontWeight: 700, color }}>{value}</div>
  </div>);
}

export default PmsNightAuditAnomaly;
